import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "./data-table";
import { useGlobalLoader } from "./GlobalLoader";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { DialogoAlerta } from "./alert-dialog";

import type { ImpressaoConfigResponse } from "@/types/impressao-config-response";
import { alert } from "@/contexts/AlertModalContext";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Copy,
  Inbox,
  Pencil,
  Plus,
  Settings,
  Star,
  Trash2,
} from "lucide-react";

type PerfilModuloComponentContextType = {};

const PerfilModuloComponentContext = createContext<
  PerfilModuloComponentContextType | undefined
>(undefined);

export function PerfilModuloComponentProvider() {
  return (
    <PerfilModuloComponentContext.Provider value={{}}>
      <PerfilModuloComponent />
    </PerfilModuloComponentContext.Provider>
  );
}

export function usePerfilModuloComponent() {
  const context = useContext(PerfilModuloComponentContext);
  if (!context) {
    throw new Error(
      "usePerfilModuloComponent deve ser usado dentro de PerfilModuloComponentProvider",
    );
  }
  return context;
}

/**
 * Célula de ações isolada em um componente para evitar unmounts durante digitação.
 */
function ActionsCell({
  item,
  isLoading,
  onDefinirPadrao,
  onSolicitarApagar,
  onClonar,
  onEditar,
}: {
  item: ImpressaoConfigResponse;
  isLoading: boolean;
  onDefinirPadrao: (id: string) => void;
  onSolicitarApagar: (item: ImpressaoConfigResponse) => void;
  onClonar: (
    id_conta: string,
    config: ImpressaoConfigResponse,
  ) => Promise<void>;
  onEditar: (id_conta: string, id: string, novoNome: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [openEditar, setOpenEditar] = useState(false);
  const [nomeEdit, setNomeEdit] = useState(item.descricao_config || "");

  const [openClonar, setOpenClonar] = useState(false);
  const [nomeClone, setNomeClone] = useState<string>("");

  const isPadrao = !!item.configuracao_em_uso;

  return (
    <TooltipProvider delayDuration={250}>
      <div className="flex items-center gap-1 justify-end">
        {/* Tornar padrão */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              disabled={isLoading || isPadrao}
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                if (item._id) onDefinirPadrao(item._id);
                else alert.error(t("perfilModulo.configIdNotFound"));
              }}
              aria-label={t("perfilModulo.makeDefault")}
            >
              <Star
                className={`h-4 w-4 ${
                  isPadrao
                    ? "fill-amber-500 text-amber-500"
                    : "text-muted-foreground"
                }`}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isPadrao
              ? t("perfilModulo.alreadyDefault")
              : t("perfilModulo.makeDefault")}
          </TooltipContent>
        </Tooltip>

        {/* Editar nome */}
        <Popover
          open={openEditar}
          onOpenChange={(open) => {
            setOpenEditar(open);
            if (open) setNomeEdit(item.descricao_config || "");
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  disabled={isLoading}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t("perfilModulo.edit")}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>{t("perfilModulo.editName")}</TooltipContent>
          </Tooltip>
          <PopoverContent side="bottom" align="end" className="w-72">
            <div className="flex flex-col gap-3">
              <Label className="text-xs">{t("perfilModulo.profileName")}</Label>
              <Input
                value={nomeEdit}
                onChange={(e) => setNomeEdit(e.target.value)}
                placeholder={t("perfilModulo.typeProfileName")}
                autoFocus
              />
              <Button
                size="sm"
                onClick={async () => {
                  if (!nomeEdit.trim()) {
                    alert.error(t("perfilModulo.nameEmpty"));
                    return;
                  }
                  if (item._id && item.id_conta) {
                    try {
                      await onEditar(
                        item.id_conta,
                        item._id,
                        nomeEdit.trim(),
                      );
                      alert.success(t("perfilModulo.updateSuccess"));
                      setOpenEditar(false);
                    } catch {
                      alert.error(t("perfilModulo.editError"));
                    }
                  } else {
                    alert.error(t("perfilModulo.invalidIdEdit"));
                  }
                }}
              >
                {t("perfilModulo.save")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Clonar */}
        <Popover
          open={openClonar}
          onOpenChange={(open) => {
            setOpenClonar(open);
            if (open) {
              setNomeClone(
                nomeClone && nomeClone.trim().length > 0
                  ? nomeClone
                  : `${item.descricao_config || "Perfil clonado"} ${t("perfilModulo.cloneSuffix")}`,
              );
            }
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  disabled={isLoading}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t("perfilModulo.clone")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>{t("perfilModulo.cloneProfile")}</TooltipContent>
          </Tooltip>
          <PopoverContent side="bottom" align="end" className="w-72">
            <div className="flex flex-col gap-3">
              <Label className="text-xs">
                {t("perfilModulo.newProfileName")}
              </Label>
              <Input
                value={nomeClone}
                onChange={(e) => setNomeClone(e.target.value)}
                placeholder={t("perfilModulo.typeNewProfileName")}
                autoFocus
              />
              <Button
                size="sm"
                onClick={async () => {
                  if (item._id && item.id_conta) {
                    try {
                      await onClonar(item.id_conta, {
                        ...item,
                        descricao_config:
                          (nomeClone && nomeClone.trim()) ||
                          t("perfilModulo.clonedProfileFallback"),
                      });
                      alert.success(t("perfilModulo.cloneSuccess"));
                      setNomeClone("");
                      setOpenClonar(false);
                    } catch {
                      alert.error(t("perfilModulo.cloneError"));
                    }
                  } else {
                    alert.error(t("perfilModulo.invalidIdClone"));
                  }
                }}
              >
                {t("perfilModulo.clone")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Apagar */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              disabled={isLoading || isPadrao}
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-500/10 disabled:text-muted-foreground"
              onClick={() => onSolicitarApagar(item)}
              aria-label={t("perfilModulo.delete")}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isPadrao
              ? t("perfilModulo.cannotDeleteDefault")
              : t("perfilModulo.deleteProfile")}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

function PerfilModuloComponent() {
  const { t } = useTranslation();
  const context = useContext(PerfilModuloComponentContext);
  if (!context) return null;
  const { isLoading } = useGlobalLoader();

  const {
    criarImpressaoConfigPadrao,
    listaImpressaoConfig,
    definirConfiguracaoComoPadrao,
    apagarImpressaoConfig,
    clonarImpressaoConfig,
    editarImpressaoConfig,
  } = useImpressaoConfig();

  // Dialog de apagar (agora funcional — antes era código morto)
  const [perfilParaApagar, setPerfilParaApagar] =
    useState<ImpressaoConfigResponse | null>(null);
  const [isOpenAlertDialog, setIsOpenAlertDialog] = useState(false);

  const solicitarApagar = (item: ImpressaoConfigResponse) => {
    setPerfilParaApagar(item);
    setIsOpenAlertDialog(true);
  };

  const confirmarApagar = async () => {
    if (!perfilParaApagar?._id || !perfilParaApagar?.id_conta) {
      alert.error(t("perfilModulo.idAccountNotFound"));
      return;
    }
    try {
      await apagarImpressaoConfig(
        perfilParaApagar.id_conta,
        perfilParaApagar._id,
      );
      alert.success(t("perfilModulo.deleteSuccess"));
    } catch {
      alert.error(t("perfilModulo.deleteError"));
    } finally {
      setIsOpenAlertDialog(false);
      setPerfilParaApagar(null);
    }
  };

  // Novo perfil
  const [openNovo, setOpenNovo] = useState(false);
  const [nomeNovo, setNomeNovo] = useState("");

  const handleSaveNovo = async () => {
    try {
      await criarImpressaoConfigPadrao(false, nomeNovo || "Novo Perfil");
      alert.success(t("perfilModulo.createSuccess"));
      setNomeNovo("");
      setOpenNovo(false);
    } catch {
      alert.error(t("perfilModulo.createError"));
    }
  };

  const handleEditar = async (
    id_conta: string,
    id: string,
    novoNome: string,
  ) => {
    try {
      await editarImpressaoConfig(id_conta, id, novoNome);
    } catch {
      alert.error(t("perfilModulo.editErrorRetry"));
    }
  };

  // ---- Normalização: se houver >1 perfil marcado como padrão, mantém o
  //      primeiro e desmarca os outros. Evita estado inconsistente do BD. ----
  const lastCleanupKey = useRef<string>("");
  useEffect(() => {
    if (!listaImpressaoConfig.length) return;

    const padroes = listaImpressaoConfig.filter((c) => c.configuracao_em_uso);
    if (padroes.length <= 1) return;

    // Identifica essa "situação de duplicidade" para não re-tentar
    // enquanto a API ainda não respondeu.
    const cleanupKey = padroes
      .map((p) => p._id || "")
      .sort()
      .join(",");
    if (lastCleanupKey.current === cleanupKey) return;
    lastCleanupKey.current = cleanupKey;

    const escolhido = padroes[0];
    if (!escolhido._id) return;

    console.warn(
      `[PerfilModulo] ${padroes.length} perfis estavam marcados como padrão. ` +
        `Mantendo "${escolhido.descricao_config || escolhido._id}" e desmarcando os demais.`,
    );
    alert.warning(
      t("perfilModulo.multipleDefaultWarning", {
        count: padroes.length,
        nome: escolhido.descricao_config || t("perfilModulo.noName"),
      }),
    );
    definirConfiguracaoComoPadrao(escolhido._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaImpressaoConfig]);

  const perfilPadrao = listaImpressaoConfig.find((c) => c.configuracao_em_uso);

  const columns: ColumnDef<ImpressaoConfigResponse>[] = useMemo(
    () => [
      {
        accessorKey: "descricao_config",
        header: t("perfilModulo.profile"),
        cell: ({ row }) => {
          const item = row.original;
          const isPadrao = !!item.configuracao_em_uso;
          return (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium truncate">
                {item.descricao_config || item._id}
              </span>
              {isPadrao && (
                <Badge
                  variant="outline"
                  className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20 gap-1"
                >
                  <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                  {t("perfilModulo.default")}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <div className="text-right">{t("perfilModulo.actions")}</div>,
        cell: ({ row }) => {
          const item = row.original;
          return (
            <ActionsCell
              item={item}
              isLoading={isLoading}
              onDefinirPadrao={definirConfiguracaoComoPadrao}
              onSolicitarApagar={solicitarApagar}
              onClonar={clonarImpressaoConfig}
              onEditar={handleEditar}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLoading,
      definirConfiguracaoComoPadrao,
      apagarImpressaoConfig,
      clonarImpressaoConfig,
      editarImpressaoConfig,
    ],
  );

  return (
    <div className="space-y-4">
      <DialogoAlerta
        description={
          perfilParaApagar
            ? t("perfilModulo.deleteConfirm", {
                nome:
                  perfilParaApagar.descricao_config ||
                  t("perfilModulo.noName"),
              })
            : t("perfilModulo.deleteConfirmGeneric")
        }
        title={t("perfilModulo.deleteConfigTitle")}
        isOpen={isOpenAlertDialog}
        onConfirm={confirmarApagar}
        onOpenChange={() => {
          setIsOpenAlertDialog(false);
          setPerfilParaApagar(null);
        }}
        key="dialogo-alerta-apagar-perfil"
      />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("perfilModulo.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("perfilModulo.subtitle")}
            {perfilPadrao && (
              <>
                {t("perfilModulo.defaultLabel")}
                <strong className="text-foreground">
                  {perfilPadrao.descricao_config || perfilPadrao._id}
                </strong>
              </>
            )}
          </p>
        </div>

        <Popover
          open={openNovo}
          onOpenChange={(open) => {
            if (open) setNomeNovo("Novo Perfil");
            setOpenNovo(open);
          }}
        >
          <PopoverTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t("perfilModulo.newProfile")}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="end" className="w-72">
            <div className="flex flex-col gap-3">
              <Label className="text-xs">{t("perfilModulo.profileName")}</Label>
              <Input
                value={nomeNovo}
                onChange={(e) => setNomeNovo(e.target.value)}
                placeholder={t("perfilModulo.typeProfileName")}
                autoFocus
              />
              <Button size="sm" onClick={handleSaveNovo}>
                <Plus className="h-4 w-4" />
                {t("perfilModulo.createProfile")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Card com tabela */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">
                {t("perfilModulo.registeredProfiles")}
              </CardTitle>
            </div>
            <Badge variant="secondary" className="font-medium">
              {t("perfilModulo.profilesCount", {
                count: listaImpressaoConfig.length,
              })}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {listaImpressaoConfig.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <Inbox className="h-6 w-6" />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-medium text-foreground">
                  {t("perfilModulo.noProfiles")}
                </p>
                <p className="text-xs">{t("perfilModulo.noProfilesDesc")}</p>
              </div>
              <Button size="sm" onClick={() => setOpenNovo(true)}>
                <Plus className="h-4 w-4" />
                {t("perfilModulo.createFirstProfile")}
              </Button>
            </div>
          ) : (
            <DataTable columns={columns} data={listaImpressaoConfig} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PerfilModuloComponent;
